import { useState, useCallback } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { JukeboxClienteProvider, useJukeboxCliente } from '../contexts/JukeboxClienteContext';
import JukeboxBottomNav from '../components/JukeboxBottomNav';
import JukeboxLoginSheet from '../components/JukeboxLoginSheet';
import JukeboxPlanoGate from '../components/JukeboxPlanoGate';
import { PolaroidCapture } from '@/features/polaroid';
import { useTecladoVirtual } from '@/shared/hooks/useTecladoVirtual';
import { isPlayerPresente } from '@/shared/lib/telaStatus';
import { jukeboxRepository } from '../../data/jukeboxRepository';

/**
 * Renderizado fora de qualquer aba (Home/Histórico/Jogo/Perfil) — nunca
 * desmonta ao navegar entre elas. `empresaId` é preenchido pela página Home
 * assim que ela monta (ver JukeboxCliente.tsx) e fica disponível aqui mesmo
 * depois que o usuário navega pra outra aba, então o botão de foto funciona
 * em qualquer aba sem precisar voltar pra Home.
 */
function JukeboxLayoutInner({ telaId }: { telaId: string }) {
  const { sessionId, ultimaAtividadePlayer } = useJukeboxCliente();

  // Um único listener de visualViewport para o app inteiro — este componente
  // nunca desmonta ao trocar de aba.
  const tecladoAberto = useTecladoVirtual();

  const [showPolaroid, setShowPolaroid] = useState(false);
  const [empresaId,    setEmpresaId]    = useState<string | null>(null);

  // Presença conferida na hora do toque: heartbeat fresco do fetch OU o carimbo
  // de broadcast que o contexto guarda (a Home desmonta ao trocar de aba).
  const onFoto = useCallback(() => {
    jukeboxRepository.buscarTela(telaId)
      .then(tela => {
        if (isPlayerPresente(tela.ultima_conexao, ultimaAtividadePlayer)) setShowPolaroid(true);
      })
      .catch(() => {});
  }, [telaId, ultimaAtividadePlayer]);

  return (
    <>
      <Outlet context={{ telaId, empresaId, setEmpresaId, tecladoAberto }} />
      <JukeboxBottomNav telaId={telaId} onFoto={onFoto} oculta={tecladoAberto} />
      <JukeboxLoginSheet />
      {showPolaroid && empresaId && (
        <PolaroidCapture
          telaId={telaId}
          empresaId={empresaId}
          sessionId={sessionId}
          autoOpenCamera
          onClose={() => setShowPolaroid(false)}
        />
      )}
    </>
  );
}

export default function JukeboxLayout() {
  const { telaId } = useParams<{ telaId: string }>();

  if (!telaId) return null;

  return (
    <JukeboxPlanoGate telaId={telaId}>
      <JukeboxClienteProvider telaId={telaId}>
        <JukeboxLayoutInner telaId={telaId} />
      </JukeboxClienteProvider>
    </JukeboxPlanoGate>
  );
}

export interface JukeboxOutletContext {
  telaId: string;
  empresaId: string | null;
  setEmpresaId: (v: string | null) => void;
  tecladoAberto: boolean;
}
